import { c as _c } from "react/compiler-runtime";
import { Avatar, Badge, Box, HStack, Link, Stack, Text, VStack, Wrap } from '@chakra-ui/react';
import { SessionReviewStars } from '@src/components/sco/Advisors/reviews/SessionReviewStars';
import { SESSION_REVIEW_SOURCE_CLARITY, sessionReviewAuthorHref, sessionReviewAuthorName, sessionReviewSourceLabel, type SessionReviewItem } from '@src/components/sco/Advisors/reviews/sessionReviewHelpers';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import moment from 'moment';
import { ReactNode } from 'react';
export type SessionReviewCardProps = {
  review: SessionReviewItem;
  /**
   * Manage view (reviewee): show "Hidden" / "Hidden by Startups" / "Text hidden" markers and the
   * hidden text itself (dimmed). Public views leave this off — the backend already strips them.
   */
  showManageState?: boolean;
  /** Row actions (manage view). */
  actions?: ReactNode;
};

/** One review: author, stars, date, source label, featured badge, text when present. */
export const SessionReviewCard = t0 => {
  const $ = _c(80);
  const {
    review,
    showManageState: t1,
    actions
  } = t0;
  const showManageState = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== review) {
    t2 = sessionReviewAuthorName(review);
    $[0] = review;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const name = t2;
  let t3;
  if ($[2] !== review.author) {
    t3 = sessionReviewAuthorHref(review.author);
    $[2] = review.author;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const href = t3;
  let t4;
  if ($[4] !== review.author?.tagline) {
    t4 = review.author?.tagline?.trim() || null;
    $[4] = review.author?.tagline;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const tagline = t4;
  let t5;
  if ($[6] !== review.createdAt) {
    t5 = review.createdAt ? moment(review.createdAt).format("MMM D, YYYY") : null;
    $[6] = review.createdAt;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const date = t5;
  const hidden = showManageState && (review.isHidden || review.isHiddenByAdmin);
  let t6;
  if ($[8] !== review.text) {
    t6 = review.text?.trim() || null;
    $[8] = review.text;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const text = t6;
  const t7 = `Review by ${name}`;
  const t8 = hidden ? "border.emphasized" : "colorPalette.border/50";
  const t9 = hidden ? "dashed" : "solid";
  const t10 = hidden ? "true" : undefined;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      base: 3,
      md: 4
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== review.author) {
    t12 = review.author?.imageUrl ? <Avatar.Image alt="" src={review.author.imageUrl} /> : null;
    $[11] = review.author;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== name) {
    t13 = <Avatar.Fallback name={name} />;
    $[13] = name;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t12 || $[16] !== t13) {
    t14 = <Avatar.Root flexShrink={0} size="sm">{t12}{t13}</Avatar.Root>;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== href || $[19] !== name) {
    t15 = href ? <Link asChild={true} color="inherit" fontSize="sm" fontWeight="semibold"><NextLink href={href}>{name}</NextLink></Link> : <Text fontSize="sm" fontWeight="semibold">{name}</Text>;
    $[18] = href;
    $[19] = name;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== tagline) {
    t16 = tagline && <Text color="fg.muted" fontSize="xs" lineClamp={2}>{tagline}</Text>;
    $[21] = tagline;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t15 || $[24] !== t16) {
    t17 = <VStack align="flex-start" flex={1} gap={0.5} minWidth={0}>{t15}{t16}</VStack>;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t14 || $[27] !== t17) {
    t18 = <HStack align="flex-start" gap={3}>{t14}{t17}</HStack>;
    $[26] = t14;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  let t20;
  let t21;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      base: "flex-start",
      sm: "center"
    };
    t20 = {
      base: "column",
      sm: "row"
    };
    t21 = {
      base: 1.5,
      sm: 2
    };
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
  } else {
    t19 = $[29];
    t20 = $[30];
    t21 = $[31];
  }
  let t22;
  if ($[32] !== review.score) {
    t22 = <SessionReviewStars score={review.score} />;
    $[32] = review.score;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== date) {
    t23 = date && <Text color="fg.muted" fontSize="xs">{date}</Text>;
    $[34] = date;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  const t24 = review.sourceKey === SESSION_REVIEW_SOURCE_CLARITY ? "blue" : "gray";
  let t25;
  if ($[36] !== review.sourceKey) {
    t25 = sessionReviewSourceLabel(review.sourceKey);
    $[36] = review.sourceKey;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== t24 || $[39] !== t25) {
    t26 = <Badge colorPalette={t24} size="sm" variant="subtle">{t25}</Badge>;
    $[38] = t24;
    $[39] = t25;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== review.isFeatured) {
    t27 = review.isFeatured && <Badge colorPalette="orange" size="sm" variant="subtle">Featured</Badge>;
    $[41] = review.isFeatured;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== review.isHidden || $[44] !== showManageState) {
    t28 = showManageState && review.isHidden && <Badge colorPalette="gray" size="sm" variant="outline">Hidden from profile</Badge>;
    $[43] = review.isHidden;
    $[44] = showManageState;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== review.isHiddenByAdmin || $[47] !== showManageState) {
    t29 = showManageState && review.isHiddenByAdmin && <Badge colorPalette="red" size="sm" variant="outline">Hidden by Startups</Badge>;
    $[46] = review.isHiddenByAdmin;
    $[47] = showManageState;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== review.isTextHidden || $[50] !== showManageState) {
    t30 = showManageState && review.isTextHidden && <Badge colorPalette="gray" size="sm" variant="outline">Text hidden</Badge>;
    $[49] = review.isTextHidden;
    $[50] = showManageState;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t23 || $[53] !== t26 || $[54] !== t27 || $[55] !== t28 || $[56] !== t29 || $[57] !== t30) {
    t31 = <Wrap align="center" gap={2}>{t23}{t26}{t27}{t28}{t29}{t30}</Wrap>;
    $[52] = t23;
    $[53] = t26;
    $[54] = t27;
    $[55] = t28;
    $[56] = t29;
    $[57] = t30;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== t22 || $[60] !== t31) {
    t32 = <Stack align={t19} direction={t20} gap={t21}>{t22}{t31}</Stack>;
    $[59] = t22;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== hidden || $[63] !== review.isTextHidden || $[64] !== showManageState || $[65] !== text) {
    t33 = text && <Text color={showManageState && review.isTextHidden ? "fg.muted" : "fg"} fontSize="sm" fontStyle={showManageState && review.isTextHidden ? "italic" : undefined} opacity={hidden ? 0.8 : 1} whiteSpace="pre-line" wordBreak="break-word">{text}</Text>;
    $[62] = hidden;
    $[63] = review.isTextHidden;
    $[64] = showManageState;
    $[65] = text;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  let t34;
  if ($[67] !== actions) {
    t34 = actions && <Box borderColor="colorPalette.border/50" borderTopWidth="1px" paddingTop={3}>{actions}</Box>;
    $[67] = actions;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] !== t18 || $[70] !== t32 || $[71] !== t33 || $[72] !== t34) {
    t35 = <VStack align="stretch" gap={3}>{t18}{t32}{t33}{t34}</VStack>;
    $[69] = t18;
    $[70] = t32;
    $[71] = t33;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== t10 || $[75] !== t35 || $[76] !== t7 || $[77] !== t8 || $[78] !== t9) {
    t36 = <Box aria-label={t7} as="article" bgColor="colorPalette.card" borderColor={t8} borderRadius="xl" borderStyle={t9} borderWidth="1px" data-hidden={t10} padding={t11} width="full">{t35}</Box>;
    $[74] = t10;
    $[75] = t35;
    $[76] = t7;
    $[77] = t8;
    $[78] = t9;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  return t36;
};